import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Fab,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Slider,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Delete,
  Visibility,
  Edit,
  Save,
  Cancel,
  Inventory,
  QrCodeScanner,
  Info,
  Store,
  CalendarToday,
  Person,
  AttachMoney,
  Upload,
  FileUpload
} from '@mui/icons-material';
import { useAppData } from '../../context/AppDataContext';
import { getOrderDetail, createOrder, updateOrder, lookupSKU, lookupManySKU, deleteOrder, updateOrderPurchases, processImportOrder, getAllActiveTags } from '../../services/api';
import { useFlashMessage } from '../../hooks/useFlashMessage';
import { useDeviceSelection } from '../../hooks/useDeviceSelection';
import { useMQTT } from '../../hooks/useMQTT';
import { getSelectedRfidDevice, saveSelectedRfidDevice } from '../../utils/rfidDeviceUtils';
import { FEATURE_PERMISSIONS } from '../../utils/frontendPermissions';

const CLIENT_BRANCH_NAMES = {
  338614: '33 Đồng Khởi',
  338669: 'Warehouse Cát Lái'
};

const getImportBranchDisplay = (clientId) => {
  if (clientId === undefined || clientId === null || clientId === '') {
    return 'N/A';
  }

  return CLIENT_BRANCH_NAMES[Number(clientId)] || String(clientId);
};

// Product Detail Modal Component for viewing/editing individual products
const ProductDetailModal = ({ open, onClose, product, onUpdateProduct, allOrderSerials = [], orderStatus = 'pending', orderId = null, allProducts = [], onRefreshData, activeTags = [] }) => {
  const { showError, showSuccess } = useFlashMessage();
  const { hasFrontendPermission } = useAppData();
  const { selectedDeviceObjects, getDeviceName } = useDeviceSelection();
  const { subscribe, unsubscribe, isConnected, subscribedTopics } = useMQTT();

  // Check permissions
  const canEdit = hasFrontendPermission(FEATURE_PERMISSIONS.INVENTORY_IMPORT.EDIT);
  const canEditSerials = hasFrontendPermission(FEATURE_PERMISSIONS.INVENTORY_IMPORT.EDIT_SERIALS);

  const [formData, setFormData] = useState({
    SKU: '',
    name: '',
    id: '',
    qty: 1,
    serials: [],
    defects: [],
    rfidDevice: ''
  });
  const [newSerial, setNewSerial] = useState('');
  const [newDefect, setNewDefect] = useState('');
  const [skipSerial, setSkipSerial] = useState([]);
  const [inputRef, setInputRef] = useState(null);
  const [defectInputRef, setDefectInputRef] = useState(null);
  const [currentDeviceTopic, setCurrentDeviceTopic] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Functions to manage skipSerial array
  const addToSkipSerial = (serial) => {
    if (!skipSerial.includes(serial)) {
      setSkipSerial(prev => [...prev, serial]);
    }
  };

  const removeFromSkipSerial = (serial) => {
    setSkipSerial(prev => prev.filter(s => s !== serial));
  };

  const clearSkipSerial = () => {
    setSkipSerial([]);
  };

  // Get RFID devices from selected devices in localStorage
  const rfidDevices = selectedDeviceObjects;

  useEffect(() => {
    if (product) {
      // Load RFID device from localStorage using utility function
      const savedRfidDevice = getSelectedRfidDevice('import');

      // Normalize defects field (support both Defects and defects)
      const normalizedDefects = product.defects || product.Defects || [];

      setFormData({
        SKU: product.SKU || '',
        name: product.name || '',
        id: product.id || product._id || '',
        qty: product.qty || 1,
        serials: [...new Set(product.serials || [])],
        defects: normalizedDefects,
        rfidDevice: savedRfidDevice
      });
    }
  }, [product]);

  // Subscribe to MQTT topic when modal opens and device is selected
  useEffect(() => {
    if (open && formData.rfidDevice && isConnected) {
      const deviceTopic = formData.rfidDevice;
      subscribe(deviceTopic, (topic, message) => {
        // Message received from device
      });
      setCurrentDeviceTopic(deviceTopic);
    }

    // Cleanup: unsubscribe when modal closes
    return () => {
      if (currentDeviceTopic) {
        unsubscribe(currentDeviceTopic);
        setCurrentDeviceTopic(null);
      }
    };
  }, [open, formData.rfidDevice, isConnected, subscribe, unsubscribe, currentDeviceTopic]);

  // Listen to MQTT messages via custom event
  useEffect(() => {
    if (!open) return;

    // Function to handle MQTT messages
    const handleMqttMessage = (event) => {
      const { topic, message, packet, timestamp } = event.detail;
      // Only process messages from the current selected device
      if (topic === currentDeviceTopic) {

        let scannedSerial = '';

        // Handle RFID scan result
        if (typeof message === 'string') {
          // If message is a string, treat it as a serial number
          scannedSerial = message.trim();
        } else if (typeof message === 'object' && message.serial) {
          // If message is an object with serial property
          scannedSerial = message.serial.trim();
        }

        // Process the scanned serial with validation
        if (scannedSerial) {
          // Check if serial already exists in current product
          if (formData.serials.includes(scannedSerial)) {
            return;
          }

          // Check if serial already exists in any other product in the order
          if (allOrderSerials.includes(scannedSerial)) {
            return;
          }

          // Check if serial is in skip list
          if (skipSerial.includes(scannedSerial)) {
            return;
          }

          // Check if serial is in active tags (skip list from API)
          if (activeTags.includes(scannedSerial)) {
            return;
          }

          // Add the valid serial
          setFormData(prev => ({
            ...prev,
            serials: [...new Set([...prev.serials, scannedSerial])]
          }));
        }
      }
    };

    // Add event listener
    window.addEventListener('mqttMessage', handleMqttMessage);

    // Cleanup: remove event listener when modal closes
    return () => {
      window.removeEventListener('mqttMessage', handleMqttMessage);
    };
  }, [open, currentDeviceTopic, formData.serials, allOrderSerials, skipSerial, activeTags]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Handle RFID device selection
    if (field === 'rfidDevice') {
      // Save RFID device selection using utility function
      saveSelectedRfidDevice(value, 'import');

      // Unsubscribe from previous device topic if exists
      if (currentDeviceTopic) {
        unsubscribe(currentDeviceTopic);
      }

      // Subscribe to new device topic if device is selected and MQTT is connected
      if (value && isConnected) {
        const deviceTopic = value; // Use device ID as topic
        subscribe(deviceTopic, (topic, message) => {
          // Message received from device
        });

        setCurrentDeviceTopic(deviceTopic);
      } else {
        setCurrentDeviceTopic(null);
      }
    }
  };

  const handleAddSerial = () => {
    const trimmedSerial = newSerial.trim();

    if (!trimmedSerial) return;

    // Check if serial already exists in current product
    if (formData.serials.includes(trimmedSerial)) {
      setNewSerial('');
      // Refocus input after clearing
      setTimeout(() => {
        if (inputRef) inputRef.focus();
      }, 0);
      return;
    }

    // Check if serial already exists in any other product in the order
    if (allOrderSerials.includes(trimmedSerial)) {
      setNewSerial('');
      // Refocus input after clearing
      setTimeout(() => {
        if (inputRef) inputRef.focus();
      }, 0);
      return;
    }

    // Check if serial is in skip list
    if (skipSerial.includes(trimmedSerial)) {
      // If serial is in skip list, just clear the input
      setNewSerial('');
      // Refocus input after clearing
      setTimeout(() => {
        if (inputRef) inputRef.focus();
      }, 0);
      return;
    }

    // Check if serial is in active tags (skip list from API)
    if (activeTags.includes(trimmedSerial)) {
      // If serial is in active tags, just clear the input
      setNewSerial('');
      // Refocus input after clearing
      setTimeout(() => {
        if (inputRef) inputRef.focus();
      }, 0);
      return;
    }

    setFormData(prev => ({
      ...prev,
      serials: [...new Set([...prev.serials, trimmedSerial])]
    }));
    setNewSerial('');
  };


  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddSerial();
    }
  };

  const handleRemoveSerial = (serialToRemove) => {
    setFormData(prev => ({
      ...prev,
      serials: prev.serials.filter(serial => serial !== serialToRemove)
    }));
  };

  const handleAddDefect = () => {
    const trimmedDefect = newDefect.trim();

    if (!trimmedDefect) return;

    // Check if defect already exists
    if (formData.defects.includes(trimmedDefect)) {
      setNewDefect('');
      setTimeout(() => {
        if (defectInputRef) defectInputRef.focus();
      }, 0);
      return;
    }

    setFormData(prev => ({
      ...prev,
      defects: [...prev.defects, trimmedDefect]
    }));
    setNewDefect('');
  };

  const handleDefectKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddDefect();
    }
  };

  const handleRemoveDefect = (defectToRemove) => {
    setFormData(prev => ({
      ...prev,
      defects: prev.defects.filter(defect => defect !== defectToRemove)
    }));
  };

  const handleSave = () => {
    // Update the local product - auto-save will handle the API call
    if (onUpdateProduct) {
      const updatedProduct = {
        ...product,
        ...formData,
        qty: parseInt(formData.qty) || 1
      };
      onUpdateProduct(updatedProduct);
    }
    onClose();
  };

  const handleCancel = () => {
    if (product) {
      setFormData({
        SKU: product.SKU || '',
        name: product.name || '',
        id: product.id || product._id || '',
        qty: product.qty || 1,
        serials: [...new Set(product.serials || [])],
        defects: product.defects || [],
        rfidDevice: product.rfidDevice || ''
      });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box className="flex justify-between items-center">
          <Box>
            <Typography variant="h5" className="font-bold">
              Product Details
            </Typography>
            <Typography variant="body2" className="text-gray-600">
              {product?.name || 'Product Information'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ position: 'relative' }}>
        {/* Overlay when user doesn't have edit-serial permission */}
        {!canEditSerials && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'not-allowed',
              backdropFilter: 'blur(1px)'
            }}
          >
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                padding: 3,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                textAlign: 'center',
                maxWidth: 400
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f', mb: 1 }}>
                Access Restricted
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                You don't have permission to edit serial numbers for this product.
              </Typography>
            </Box>
          </Box>
        )}
        {/* Product Information Header */}
        <Card className="mb-6">
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <Box className="text-center">
                  <Avatar
                    src={product?.image_url}
                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                  >
                    <Inventory />
                  </Avatar>
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SKU"
                      value={formData.SKU}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Product Name"
                      value={formData.name}
                      fullWidth
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Quantity"
                      type="number"
                      value={formData.qty}
                      onChange={(e) => handleInputChange('qty', parseInt(e.target.value) || 1)}
                      fullWidth
                      inputProps={{ min: 1 }}
                      variant="outlined"
                      disabled={!canEdit}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Serial Numbers and Defects Management with Tabs */}
        <Card className="mb-6">
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label={`Serials (${formData.serials.length})`} />
                <Tab label={`Defects (${formData.defects.length})`} />
              </Tabs>
            </Box>

            {/* Tab 1: Serials */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" className="font-semibold mb-4">
                  Serial Numbers Management ({formData.serials.length})
                </Typography>

                {/* Quantity vs Serial Count Comparison (including defects) */}
                <Box className="mb-4">
                  {(() => {
                    const quantity = parseInt(formData.qty) || 0;
                    const serialCount = formData.serials.length;
                    const defectsCount = formData.defects.length;
                    const totalCount = serialCount + defectsCount;
                    const difference = totalCount - quantity;

                    if (difference === 0) {
                      return (
                        <Typography variant="body2" className="text-green-600 font-medium">
                          ✓ Quantity matches total count ({quantity} = {serialCount} serials + {defectsCount} defects)
                        </Typography>
                      );
                    } else if (difference < 0) {
                      return (
                        <Typography variant="body2" className="text-red-600 font-medium">
                          ⚠ Missing {Math.abs(difference)} item{Math.abs(difference) !== 1 ? 's' : ''} (Need {quantity}, have {totalCount} = {serialCount} serials + {defectsCount} defects)
                        </Typography>
                      );
                    } else {
                      return (
                        <Typography variant="body2" className="text-yellow-600 font-medium">
                          ⚠ Excess {difference} item{difference !== 1 ? 's' : ''} (Need {quantity}, have {totalCount} = {serialCount} serials + {defectsCount} defects)
                        </Typography>
                      );
                    }
                  })()}
                </Box>

                {/* Add New Serial */}
                {orderStatus === 'pending' && (
                  <Box className="mb-4">
                    <TextField
                      label="Add Serial Number"
                      value={newSerial}
                      onChange={(e) => setNewSerial(e.target.value)}
                      onKeyPress={handleKeyPress}
                      inputRef={(ref) => setInputRef(ref)}
                      fullWidth
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleAddSerial}
                              disabled={
                                !newSerial.trim() ||
                                formData.serials.includes(newSerial.trim()) ||
                                allOrderSerials.includes(newSerial.trim()) ||
                                skipSerial.includes(newSerial.trim()) ||
                                activeTags.includes(newSerial.trim())
                              }
                            >
                              <Add />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      helperText={
                        (() => {
                          const trimmedSerial = newSerial.trim();
                          if (!trimmedSerial) return '';
                          if (formData.serials.includes(trimmedSerial)) return 'Serial already exists in this product - will be cleared on Enter';
                          if (allOrderSerials.includes(trimmedSerial)) return 'Serial already exists in another product - will be cleared on Enter';
                          if (skipSerial.includes(trimmedSerial)) return 'Serial is in skip list - will be cleared on Enter';
                          if (activeTags.includes(trimmedSerial)) return 'Serial is in active tags (skip list) - will be cleared on Enter';
                          return '';
                        })()
                      }
                    />
                  </Box>
                )}

                {/* Serial List as Chips */}
                <Box
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    padding: 2,
                    backgroundColor: '#fafafa',
                    minHeight: 100
                  }}
                >
                  {formData.serials.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" className="text-center">
                      No serial numbers added yet
                    </Typography>
                  ) : (
                    <Box className="flex flex-wrap gap-2">
                      {formData.serials.map((serial, index) => (
                        <Chip
                          key={index}
                          label={serial}
                          onDelete={orderStatus === 'pending' ? () => handleRemoveSerial(serial) : undefined}
                          color="primary"
                          variant="outlined"
                          sx={{
                            backgroundColor: '#e3f2fd',
                            borderColor: '#2196f3',
                            '& .MuiChip-deleteIcon': {
                              color: '#666',
                              '&:hover': {
                                color: '#f44336'
                              }
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Tab 2: Defects */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" className="font-semibold mb-4">
                  Defects Management ({formData.defects.length})
                </Typography>

                {/* Add New Defect */}
                {orderStatus === 'pending' && (
                  <Box className="mb-4">
                    <TextField
                      label="Add Defect"
                      value={newDefect}
                      onChange={(e) => setNewDefect(e.target.value)}
                      onKeyPress={handleDefectKeyPress}
                      inputRef={(ref) => setDefectInputRef(ref)}
                      fullWidth
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleAddDefect}
                              disabled={!newDefect.trim() || formData.defects.includes(newDefect.trim())}
                            >
                              <Add />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      helperText={
                        newDefect.trim() && formData.defects.includes(newDefect.trim())
                          ? 'Defect already exists - will be cleared on Enter'
                          : ''
                      }
                    />
                  </Box>
                )}

                {/* Defects List as Chips */}
                <Box
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    padding: 2,
                    backgroundColor: '#fafafa',
                    minHeight: 100
                  }}
                >
                  {formData.defects.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" className="text-center">
                      No defects added yet
                    </Typography>
                  ) : (
                    <Box className="flex flex-wrap gap-2">
                      {formData.defects.map((defect, index) => (
                        <Chip
                          key={index}
                          label={defect}
                          onDelete={orderStatus === 'pending' ? () => handleRemoveDefect(defect) : undefined}
                          color="error"
                          variant="outlined"
                          sx={{
                            backgroundColor: '#ffebee',
                            borderColor: '#f44336',
                            '& .MuiChip-deleteIcon': {
                              color: '#666',
                              '&:hover': {
                                color: '#d32f2f'
                              }
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* RFID Device Selection */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="font-semibold mb-4">
              RFID Device Selection
            </Typography>
            {rfidDevices.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No RFID devices available. Please go to Settings to select devices first.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select RFID Device</InputLabel>
                <Select
                  value={formData.rfidDevice}
                  onChange={(e) => handleInputChange('rfidDevice', e.target.value)}
                  label="Select RFID Device"
                >
                  {rfidDevices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {formData.rfidDevice && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Selected Device: {getDeviceName(formData.rfidDevice)} ({formData.rfidDevice})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MQTT Status: {isConnected ? 'Connected' : 'Disconnected'}
                </Typography>
                {currentDeviceTopic && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                    Listening to topic: {currentDeviceTopic}
                  </Typography>
                )}
                {subscribedTopics.length > 0 && (
                  <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
                    Subscribed Topics: {subscribedTopics.join(', ')}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions>
        <Box className="flex gap-2 w-full justify-end">
          {orderStatus === 'pending' ? (
            <>
              <Button onClick={handleCancel} variant="outlined">
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                className="bg-blue-600"
              >
                Save
              </Button>
            </>
          ) : (
            <Button onClick={onClose} variant="outlined">
              Close
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// Product Detail Dialog Component
const ProductDetailDialog = ({ open, onClose, product, onUpdateProduct, isEdit = false, existingProducts = [] }) => {
  const [skuSearchTerm, setSkuSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    if (product) {
      // If editing existing product, populate the form
      setSelectedProducts([{
        SKU: product.SKU,
        name: product.name,
        image_url: product.image_url,
        id: product.id || product._id,
        qty: product.qty || 1
      }]);
      setSkuSearchTerm(product.SKU);
    } else {
      // Reset form for new product
      setSelectedProducts([]);
      setSkuSearchTerm('');
      setSearchResults([]);
    }
  }, [product]);

  const handleSKUSearch = async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await lookupSKU(term);
      if (response.found && response.list) {
        setSearchResults(response.list.map(item => ({
          SKU: item.sku,
          name: item.name,
          image_url: item.image,
          id: item.id
        })));
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching SKU:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchTermChange = (value) => {
    setSkuSearchTerm(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for 200ms delay
    const timeout = setTimeout(() => {
      handleSKUSearch(value);
    }, 200);

    setSearchTimeout(timeout);
  };
  window.handleSearchTermChange = handleSearchTermChange;
  const handleProductSelect = (productData) => {
    // Check if product is already selected in the dialog
    const isAlreadySelected = selectedProducts.some(p => p.id === productData.id);

    // Check if product already exists in the main product list
    const alreadyExists = existingProducts.some(p => p.id === productData.id);

    if (!isAlreadySelected && !alreadyExists) {
      setSelectedProducts(prev => [...prev, {
        ...productData,
        qty: 1
      }]);
    } else if (alreadyExists) {
      // If product already exists, show a message or handle accordingly
      // Product already exists in the order
    }

    setSearchResults([]);
  };

  const handleQuantityChange = (productId, newQuantity) => {
    setSelectedProducts(prev =>
      prev.map(product =>
        product.id === productId
          ? { ...product, qty: Math.max(1, newQuantity) }
          : product
      )
    );
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(product => product.id !== productId));
  };

  const handleSave = () => {
    if (selectedProducts.length > 0 && onUpdateProduct) {
      // If editing single product, return the first selected product
      if (product) {
        const productToSave = {
          ...selectedProducts[0],
          _id: product._id || `temp_${Date.now()}`
        };
        onUpdateProduct(productToSave);
      } else {
        // If adding new products, return all selected products as an array
        const productsToSave = selectedProducts.map(productData => ({
          ...productData,
          _id: `temp_${Date.now()}_${productData.id}`
        }));
        onUpdateProduct(productsToSave);
      }
    }
    onClose();
  };

  const handleCancel = () => {
    if (product) {
      // Reset to original product data
      setSelectedProducts([{
        SKU: product.SKU,
        name: product.name,
        image_url: product.image_url,
        id: product.id || product._id,
        qty: product.qty || 1
      }]);
      setSkuSearchTerm(product.SKU);
    } else {
      // Reset form for new product
      setSelectedProducts([]);
      setSkuSearchTerm('');
    }
    setSearchResults([]);
    onClose();
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box className="flex justify-between items-center">
          <Box>
            <Typography variant="h5" className="font-bold">
              {product ? 'Edit Product' : 'Add New Product'}
            </Typography>
            <Typography variant="body2" className="text-gray-600">
              {product ? product.name : 'Search and select products by SKU'}
            </Typography>
          </Box>
          <Box className="flex gap-2">
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              className="bg-blue-600"
              disabled={selectedProducts.length === 0}
            >
              {product ? 'Save' : `Add ${selectedProducts.length} Product${selectedProducts.length !== 1 ? 's' : ''}`}
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* SKU Search */}
        <Card className="mb-6">
          <CardContent>
            <Typography variant="h6" className="font-semibold mb-4">
              Search Product by SKU
            </Typography>
            <TextField
              label="Enter SKU"
              value={skuSearchTerm}
              onChange={(e) => handleSearchTermChange(e.target.value)}
              fullWidth
              placeholder="Type SKU to search..."
              InputProps={{
                endAdornment: isSearching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box className="mt-4">
                <Typography variant="subtitle2" className="font-semibold mb-2">
                  Search Results ({searchResults.length})
                </Typography>
                <Paper className="max-h-60 overflow-y-auto">
                  <List>
                    {searchResults.map((result, index) => {
                      const isSelected = selectedProducts.some(p => p.id === result.id);
                      const alreadyExists = existingProducts.some(p => p.id === result.id);
                      return (
                        <ListItem
                          key={index}
                          button
                          onClick={() => handleProductSelect(result)}
                          className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''} ${alreadyExists ? 'bg-red-50' : ''}`}
                          disabled={isSelected || alreadyExists}
                        >
                          <Avatar
                            src={result.image_url}
                            sx={{ width: 50, height: 50, mr: 2 }}
                          >
                            <Inventory />
                          </Avatar>
                          <ListItemText
                            primary={result.name}
                            secondary={`SKU: ${result.SKU}`}
                          />
                          {isSelected && (
                            <Chip label="Selected" size="small" color="primary" />
                          )}
                          {alreadyExists && (
                            <Chip label="Already in Order" size="small" color="error" />
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              </Box>
            )}

            {skuSearchTerm.length >= 2 && searchResults.length === 0 && !isSearching && (
              <Typography variant="body2" color="textSecondary" className="mt-2">
                No products found for SKU: {skuSearchTerm}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Selected Products */}
        {selectedProducts.length > 0 && (
          <Card className="mb-6">
            <CardContent>
              <Typography variant="h6" className="font-semibold mb-4">
                Selected Products ({selectedProducts.length})
              </Typography>
              <Paper className="max-h-60 overflow-y-auto">
                <List>
                  {selectedProducts.map((productData, index) => (
                    <ListItem key={productData.id} className="hover:bg-gray-50">
                      <Avatar
                        src={productData.image_url}
                        sx={{ width: 50, height: 50, mr: 2 }}
                      >
                        <Inventory />
                      </Avatar>
                      <ListItemText
                        primary={productData.name}
                        secondary={`SKU: ${productData.SKU} | ID: ${productData.id}`}
                      />
                      <Box className="flex items-center gap-2">
                        <TextField
                          label="Qty"
                          type="number"
                          value={productData.qty}
                          onChange={(e) => handleQuantityChange(productData.id, parseInt(e.target.value) || 1)}
                          size="small"
                          sx={{ width: 80 }}
                          inputProps={{ min: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveProduct(productData.id)}
                          color="error"
                          title="Remove Product"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const InventoryImportCreatePage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getStoreList, hasFrontendPermission } = useAppData();
  const { showError, showSuccess, message, type, isVisible, hideMessage } = useFlashMessage();

  // Check permissions
  const canEdit = hasFrontendPermission(FEATURE_PERMISSIONS.INVENTORY_IMPORT.EDIT);
  const canConfirm = hasFrontendPermission(FEATURE_PERMISSIONS.INVENTORY_IMPORT.CONFIRM);

  // Get clientId from navigation state
  const clientId = location.state?.clientId;

  // Generate default order ID using client_id + current timestamp
  const generateDefaultOrderId = () => {
    const timestamp = Date.now();
    return clientId ? `${clientId}_${timestamp}` : `ORDER_${timestamp}`;
  };

  const [orderData, setOrderData] = useState({
    unique_key: generateDefaultOrderId(),
    status: 'pending',
    refer_link: '',
    createdAt: new Date().toISOString(),
    createdBy: 'Current User',
    metaData: {},
    creatorNames: [],
    serialInputterNames: [],
    orderConfirmerNames: []
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [showProductDetailModal, setShowProductDetailModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(true);

  // CSV Import states
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvImportProgress, setCsvImportProgress] = useState('');
  const [csvImportError, setCsvImportError] = useState(null);
  const [csvImportSuccess, setCsvImportSuccess] = useState(null);

  // Missing SKUs modal states
  const [showMissingSKUsModal, setShowMissingSKUsModal] = useState(false);
  const [missingSKUs, setMissingSKUs] = useState([]);
  const [pendingValidProducts, setPendingValidProducts] = useState([]);

  // Delete order states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Order purchase creation states
  const [isCreatingOrderPurchase, setIsCreatingOrderPurchase] = useState(false);
  const [orderPurchaseCreated, setOrderPurchaseCreated] = useState(false);

  // Confirm & Proceed functionality
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmStep, setConfirmStep] = useState(null); // null, 'validation', 'processing', 'result'
  const [validationResult, setValidationResult] = useState(null); // 'success', 'error'
  const [validationMessage, setValidationMessage] = useState('');
  const [confirmButtonStep, setConfirmButtonStep] = useState(0); // 0: ready for btn1, 1: btn1 clicked waiting, 2: ready for btn2, 3: btn2 clicked waiting, 4: ready for btn3

  // Active tags state
  const [activeTags, setActiveTags] = useState([]);

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [lastSavedProductsHash, setLastSavedProductsHash] = useState('');

  // Function to generate a hash of products based on relevant fields only
  const generateProductsHash = (productsList) => {
    // Only include fields that matter for comparison: SKU, qty, serials, defects
    const relevantData = productsList.map(p => ({
      id: p.id,
      SKU: p.SKU,
      qty: p.qty || 1,
      serials: (p.serials || []).sort(), // Sort serials for consistent comparison
      defects: (p.defects || []).sort() // Sort defects for consistent comparison
    }));
    return JSON.stringify(relevantData);
  };

  // Helper function to normalize product data (convert Defects to defects)
  const normalizeProduct = (product) => {
    const normalized = { ...product };
    // Normalize Defects (capital D) to defects (lowercase d)
    if (product.Defects !== undefined && product.defects === undefined) {
      normalized.defects = product.Defects;
      // Remove Defects to avoid duplication
      delete normalized.Defects;
    } else if (product.defects === undefined) {
      normalized.defects = [];
    }
    return normalized;
  };

  // Helper function to normalize products array
  const normalizeProducts = (products) => {
    return (products || []).map(normalizeProduct);
  };

  // Load existing order if editing
  const loadOrderDetails = async () => {
    if (!orderId) return;

    setLoading(true);
    setError(null);
    try {
      const orderData = await getOrderDetail(orderId);
      if (orderData) {
        setOrderData({
          id: orderId,
          unique_key: orderData.unique_key || '',
          status: orderData.status || 'pending',
          refer_link: orderData.refer_link || '',
          createdAt: orderData.createdAt || new Date().toISOString(),
          createdBy: orderData.createdBy || 'Current User',
          metaData: orderData.metaData || {},
          creatorNames: orderData.creatorNames || [],
          serialInputterNames: orderData.serialInputterNames || [],
          orderConfirmerNames: orderData.orderConfirmerNames || []
        });
        const loadedProducts = normalizeProducts(orderData.products || []);
        setProducts(loadedProducts);
        // Set initial hash after loading
        setLastSavedProductsHash(generateProductsHash(loadedProducts));
        setIsEditMode(false); // Start in view mode for existing orders
        // Mark initial load as complete after a short delay
        setTimeout(() => {
          setInitialLoadComplete(true);
        }, 500);
      } else {
        setError('Order not found');
      }
    } catch (err) {
      setError('Failed to load order details. Please try again.');
      console.error('Error loading order details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrderDetails();
  }, [orderId]);

  // Auto-save when products change
  useEffect(() => {
    // Don't auto-save if:
    // 1. No orderId (new order not yet created)
    // 2. Initial load not complete (to avoid saving immediately after loading)
    // 3. Order is not pending (can't edit completed orders)
    // 4. Already auto-saving
    if (!orderId || !initialLoadComplete || orderData.status !== 'pending' || isAutoSaving) {
      return;
    }

    // Generate current hash of products
    const currentHash = generateProductsHash(products);

    // Only auto-save if data has actually changed
    if (currentHash === lastSavedProductsHash) {
      return;
    }

    // Debounce the auto-save with 1 second delay
    const autoSaveTimeout = setTimeout(async () => {
      try {
        setIsAutoSaving(true);

        // Format products for API call (use Defects with capital D to match API)
        const formattedProducts = products.map(product => ({
          id: product.id,
          SKU: product.SKU,
          qty: product.qty || 1,
          name: product.name,
          image_url: product.image_url || '',
          serials: product.serials || [],
          Defects: product.defects || product.Defects || [] // Use capital D for API
        }));

        await updateOrderPurchases(orderId, formattedProducts);

        // Update the saved hash after successful save
        setLastSavedProductsHash(currentHash);
        showSuccess('Changes saved automatically');
      } catch (error) {
        console.error('Error auto-saving:', error);
        const errorMessage = error.response?.data?.error_message || error.message || 'Failed to auto-save changes';
        showError(errorMessage);
      } finally {
        setIsAutoSaving(false);
      }
    }, 1000);

    // Cleanup timeout on unmount or when dependencies change
    return () => clearTimeout(autoSaveTimeout);
  }, [products, orderId, initialLoadComplete, orderData.status, isAutoSaving, lastSavedProductsHash]);

  // Monitor order ID changes and trigger order purchase creation
  useEffect(() => {
    if (orderData.unique_key && products.length > 1 && !orderId) {
      // Add a small delay to avoid multiple calls
      const timeoutId = setTimeout(() => {
        checkAndCreateOrderPurchase();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [orderData.unique_key, products.length]);

  const handleBack = () => {
    navigate('/inventory-import');
  };

  const handleViewProduct = async (product) => {
    try {
      // Fetch active tags before opening the modal
      const response = await getAllActiveTags();
      if (response && response.tags) {
        setActiveTags(response.tags);
      }
    } catch (error) {
      console.error('Error fetching active tags:', error);
      // Continue with opening modal even if API call fails
      setActiveTags([]);
    }

    setSelectedProduct(product);
    setShowProductDetailModal(true);
  };

  // Get all serial numbers from all products in the order (excluding current product)
  const getAllOrderSerials = (currentProductId) => {
    return products
      .filter(p => p._id !== currentProductId)
      .flatMap(p => p.serials || []);
  };

  const handleCloseProductDetailModal = () => {
    setShowProductDetailModal(false);
    setSelectedProduct(null);
  };

  const handleCloseProductDetail = () => {
    setShowProductDetail(false);
    setSelectedProduct(null);
  };

  const handleUpdateProductFromModal = (updatedProduct) => {
    const updatedProducts = products.map(product =>
      product._id === updatedProduct._id ? updatedProduct : product
    );
    setProducts(updatedProducts);
  };

  const handleAddNewProduct = () => {
    setSelectedProduct(null);
    setShowProductDetail(true);
  };

  const handleUpdateProduct = (updatedProduct) => {
    if (selectedProduct) {
      // Update existing product
      const updatedProducts = products.map(product =>
        product._id === updatedProduct._id ? updatedProduct : product
      );
      setProducts(updatedProducts);
    } else {
      // Add new product(s) - handle both single and multiple products
      if (Array.isArray(updatedProduct)) {
        // Handle multiple products and prevent duplicates
        setProducts(prev => {
          let newProducts = [...prev];

          updatedProduct.forEach(newProduct => {
            // Check if product with same ID already exists
            const existingIndex = newProducts.findIndex(p => p.id === newProduct.id);

            if (existingIndex !== -1) {
              // Replace existing product with new one (last input takes precedence)
              newProducts[existingIndex] = newProduct;
            } else {
              // Add new product
              newProducts.push(newProduct);
            }
          });

          return newProducts;
        });
      } else {
        // Handle single product
        setProducts(prev => {
          const existingIndex = prev.findIndex(p => p.id === updatedProduct.id);

          if (existingIndex !== -1) {
            // Replace existing product with new one (last input takes precedence)
            const newProducts = [...prev];
            newProducts[existingIndex] = updatedProduct;
            return newProducts;
          } else {
            // Add new product
            return [...prev, updatedProduct];
          }
        });
      }
    }

    // Check if we should create order purchase automatically
    checkAndCreateOrderPurchase();
  };

  // Function to check conditions and create order purchase automatically
  const checkAndCreateOrderPurchase = async () => {
    // Only create order purchase if:
    // 1. We have an order ID (unique_key)
    // 2. We have more than 1 product
    // 3. We're not in edit mode for existing order
    // 4. We haven't already created an order purchase
    // 5. We're not currently creating one
    if (orderData.unique_key && products.length > 1 && !orderId && !orderPurchaseCreated && !isCreatingOrderPurchase) {
      try {
        await createOrderPurchaseAutomatically();
      } catch (error) {
        console.error('Error creating order purchase automatically:', error);
        // Don't show error here as it might be called multiple times
      }
    }
  };

  // Function to create order purchase automatically
  const createOrderPurchaseAutomatically = async () => {
    try {
      setIsCreatingOrderPurchase(true);
      setLoading(true);

      // Format products according to API structure (use Defects with capital D to match API)
      const formattedProducts = products.map(product => ({
        id: product.id,
        SKU: product.SKU,
        qty: product.qty || 1,
        name: product.name,
        image_url: product.image_url || '',
        serials: product.serials || [],
        Defects: product.defects || product.Defects || [] // Use capital D for API
      }));

      // Format order data according to API structure
      const orderToCreate = {
        unique_key: orderData.unique_key,
        products: formattedProducts,
        status: 'pending',
        refer_link: orderData.refer_link || '',
        metaData: {
          clientId: clientId
        }
      };

      const response = await createOrder(orderToCreate);

      if (response && response._id) {
        setOrderPurchaseCreated(true);
        showSuccess('Order purchase created successfully!');
        // Navigate to update page with the created order ID
        navigate(`/inventory-import/${response._id}`, {
          state: {
            clientId: clientId,
            fromCreate: true
          }
        });
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (error) {
      console.error('Error creating order purchase:', error);
      const errorMessage = error.response?.data?.error_message || error.message || 'Failed to create order purchase';
      showError(errorMessage);
    } finally {
      setIsCreatingOrderPurchase(false);
      setLoading(false);
    }
  };

  const handleDeleteProduct = (productId) => {
    // Remove product from local state - auto-save will handle the API call
    const updatedProducts = products.filter(product => product._id !== productId);
    setProducts(updatedProducts);
  };

  const handleSaveOrder = async () => {
    try {
      setLoading(true);

      // Format products according to API structure (use Defects with capital D to match API)
      const formattedProducts = products.map(product => ({
        id: product.id,
        SKU: product.SKU,
        qty: product.qty || 1,
        name: product.name,
        image_url: product.image_url || '',
        serials: product.serials || [],
        Defects: product.defects || product.Defects || [] // Use capital D for API
      }));

      // Format order data according to API structure
      // Preserve existing metaData and merge with clientId
      const existingMetaData = orderData.metaData || {};
      const orderToSave = {
        unique_key: orderData.unique_key,
        products: formattedProducts,
        status: orderData.status,
        refer_link: orderData.refer_link || '',
        metaData: {
          ...existingMetaData,
          ...(clientId && { clientId: clientId })
        }
      };

      if (orderId) {
        // Update existing order
        await updateOrder(orderId, orderToSave);
        showSuccess('Order updated successfully!');
        // Reload order details to ensure UI is up to date
        await loadOrderDetails();
      } else {
        // Create new order
        const response = await createOrder(orderToSave);
        showSuccess('Order created successfully!');
        // If order was created, navigate to the edit page
        if (response && response._id) {
          navigate(`/inventory-import/${response._id}`, {
            state: {
              clientId: clientId,
              fromCreate: true
            }
          });
        } else {
          // Fallback: stay on current page if no _id in response
          showError('Order created but could not retrieve order ID. Please refresh the page.');
        }
      }
    } catch (error) {
      console.error('Error saving order:', error);
      const errorMessage = error.response?.data?.error_message || error.message || 'Failed to save order. Please try again.';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (orderId) {
      loadOrderDetails(); // Reload original data
    } else {
      // Clear form for new order with default order ID
      setOrderData({
        unique_key: generateDefaultOrderId(),
        status: 'pending',
        refer_link: '',
        createdAt: new Date().toISOString(),
        createdBy: 'Current User',
        metaData: {},
        creatorNames: [],
        serialInputterNames: [],
        orderConfirmerNames: []
      });
      setProducts([]);
    }
    setIsEditMode(false);
  };

  // CSV Import Functions
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const skuIndex = headers.findIndex(h => h.includes('sku'));
    const qtyIndex = headers.findIndex(h => h.includes('qty') || h.includes('quantity'));

    if (skuIndex === -1) {
      throw new Error('CSV file must contain a SKU column');
    }
    if (qtyIndex === -1) {
      throw new Error('CSV file must contain a QTY or Quantity column');
    }

    const csvData = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const sku = values[skuIndex];
      const qty = parseInt(values[qtyIndex]) || 1;

      if (sku) {
        csvData.push({ SKU: sku, qty: qty });
      }
    }

    return csvData;
  };

  const handleCSVImport = async (file) => {
    if (!file) return;

    setCsvImportLoading(true);
    setCsvImportError(null);
    setCsvImportSuccess(null);
    setCsvImportProgress('Reading CSV file...');

    try {
      const csvText = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      setCsvImportProgress('Parsing CSV data...');
      const csvData = parseCSV(csvText);

      if (csvData.length === 0) {
        throw new Error('No valid data found in CSV file');
      }

      setCsvImportProgress(`Looking up ${csvData.length} products...`);

      // Prepare data for batch lookup API
      const lookupData = csvData.map(item => ({
        sku: item.SKU,
        qty: parseInt(item.qty) || 1
      }));

      // Call batch lookup API
      const response = await lookupManySKU(lookupData);

      if (!response.list || response.list.length === 0) {
        throw new Error('No products found for the provided SKUs');
      }

      setCsvImportProgress('Processing products...');

      // Check for missing SKUs (SKUs in CSV that weren't returned by API)
      const returnedSKUs = response.list.map(p => p.sku);
      const requestedSKUs = csvData.map(item => item.SKU);
      const notFoundSKUs = requestedSKUs.filter(sku => !returnedSKUs.includes(sku));

      // Convert API response to product format
      const validProducts = response.list.map(productData => ({
        SKU: productData.sku,
        name: productData.name,
        image_url: productData.image,
        id: productData.id,
        qty: productData.qty,
        _id: `temp_${Date.now()}_${productData.id}`,
        serials: []
      }));

      // If there are missing SKUs, show modal
      if (notFoundSKUs.length > 0) {
        setCsvImportLoading(false);
        setCsvImportProgress('');
        setMissingSKUs(notFoundSKUs);
        setPendingValidProducts(validProducts);
        setShowMissingSKUsModal(true);
        return;
      }

      setCsvImportProgress('Adding products to order...');

      // Handle duplicates: replace quantity but keep existing serials
      setProducts(prev => {
        let updatedProducts = [...prev];

        validProducts.forEach(newProduct => {
          const existingIndex = updatedProducts.findIndex(p => p.id === newProduct.id);

          if (existingIndex !== -1) {
            // Product exists: update quantity but keep existing serials
            updatedProducts[existingIndex] = {
              ...updatedProducts[existingIndex],
              qty: newProduct.qty
            };
          } else {
            // New product: add to list
            updatedProducts.push(newProduct);
          }
        });

        return updatedProducts;
      });

      const newProductsCount = validProducts.filter(p =>
        !products.some(existing => existing.id === p.id)
      ).length;

      const updatedProductsCount = validProducts.length - newProductsCount;

      let successMessage = '';
      if (newProductsCount > 0 && updatedProductsCount > 0) {
        successMessage = `Successfully imported ${newProductsCount} new products and updated ${updatedProductsCount} existing products`;
      } else if (newProductsCount > 0) {
        successMessage = `Successfully imported ${newProductsCount} new products`;
      } else if (updatedProductsCount > 0) {
        successMessage = `Successfully updated ${updatedProductsCount} existing products`;
      } else {
        successMessage = 'All products from CSV already exist in the order';
      }

      setCsvImportSuccess(successMessage);

    } catch (error) {
      console.error('CSV import error:', error);
      setCsvImportError(error.message || 'Failed to import CSV file');
    } finally {
      setCsvImportLoading(false);
      setCsvImportProgress('');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      handleCSVImport(file);
    } else {
      setCsvImportError('Please select a valid CSV file');
    }
    // Reset file input
    event.target.value = '';
  };

  // Handle missing SKUs modal actions
  const handleSkipAndContinue = () => {
    // Add the valid products to the list
    let updatedProductsList = [];
    setProducts(prev => {
      updatedProductsList = [...prev];

      pendingValidProducts.forEach(newProduct => {
        const existingIndex = updatedProductsList.findIndex(p => p.id === newProduct.id);

        if (existingIndex !== -1) {
          // Product exists: update quantity but keep existing serials
          updatedProductsList[existingIndex] = {
            ...updatedProductsList[existingIndex],
            qty: newProduct.qty
          };
        } else {
          // New product: add to list
          updatedProductsList.push(newProduct);
        }
      });

      return updatedProductsList;
    });

    const newProductsCount = pendingValidProducts.filter(p =>
      !products.some(existing => existing.id === p.id)
    ).length;

    const updatedProductsCount = pendingValidProducts.length - newProductsCount;

    let successMessage = '';
    if (newProductsCount > 0 && updatedProductsCount > 0) {
      successMessage = `Successfully imported ${newProductsCount} new products and updated ${updatedProductsCount} existing products. ${missingSKUs.length} SKUs were not found.`;
    } else if (newProductsCount > 0) {
      successMessage = `Successfully imported ${newProductsCount} new products. ${missingSKUs.length} SKUs were not found.`;
    } else if (updatedProductsCount > 0) {
      successMessage = `Successfully updated ${updatedProductsCount} existing products. ${missingSKUs.length} SKUs were not found.`;
    }

    showSuccess(successMessage);

    // Close modal and reset states
    setShowMissingSKUsModal(false);
    setMissingSKUs([]);
    setPendingValidProducts([]);
  };

  const handleClearData = () => {
    // Close modal and reset states without adding products
    setShowMissingSKUsModal(false);
    setMissingSKUs([]);
    setPendingValidProducts([]);
    showError('Import cancelled. Please fix the CSV file and try again.');
  };

  // Delete Order Functions
  const handleDeleteOrder = async () => {
    if (!orderId) return;

    setDeleteLoading(true);
    try {
      await deleteOrder(orderId);
      alert('Order deleted successfully!');
      navigate('/inventory-import');
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order. Please try again.');
    } finally {
      setDeleteLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'done':
        return 'success';
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
      case 'done':
        return 'Completed';
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTotalItems = () => {
    return products.reduce((total, product) => total + (product.qty || 0), 0);
  };

  // Check if Confirm & Proceed button should be disabled
  const isConfirmButtonDisabled = () => {
    // No products
    if (products.length === 0) return true;

    // At least one product has actual qty = 0 (including defects)
    const hasZeroActualQty = products.some(product => {
      const serialCount = product.serials ? product.serials.length : 0;
      const defectsCount = (product.defects || product.Defects || []).length;
      return (serialCount + defectsCount) === 0;
    });
    if (hasZeroActualQty) return true;

    // More than 20% products are not matching (including defects)
    const notMatchingProducts = products.filter(product => {
      const serialCount = product.serials ? product.serials.length : 0;
      const defectsCount = (product.defects || product.Defects || []).length;
      const totalCount = serialCount + defectsCount;
      return (product.qty || 0) !== totalCount;
    });
    const notMatchingPercentage = (notMatchingProducts.length / products.length) * 100;
    if (notMatchingPercentage > 20) return true;

    return false;
  };

  // Handle Confirm & Proceed button click
  const handleConfirmAndProceed = () => {
    setShowConfirmDialog(true);
    setConfirmStep(null);
    setConfirmButtonStep(0);
  };

  // Handle API call for processing import order
  const handleProcessImportOrder = async () => {
    try {
      setConfirmStep('validation');
      setValidationMessage('Validating data...');

      // Show validation for exactly 2 seconds
      setTimeout(async () => {
        setConfirmStep('processing');
        setValidationMessage('Processing import order...');

        try {
          // Call the API to process the import order
          const response = await processImportOrder(orderData.id);

          // Show success result
          setTimeout(() => {
            setConfirmStep('result');
            setValidationResult('success');
            setValidationMessage('Import order processed successfully!');

            // Show success message
            showFlashMessage('Import order processed successfully!', 'success');

            // Redirect to orders list after 2 seconds
            setTimeout(() => {
              navigate('/inventory-import');
            }, 2000);
          }, 3000);

        } catch (error) {
          console.error('Error processing import order:', error);

          // Show error result
          setConfirmStep('result');
          setValidationResult('error');
          setValidationMessage(`Failed to process import order. Please try again. ${JSON.stringify(error.message, null, 2)}`);

          // Show error message
          showFlashMessage(`Failed to process import order. Please try again. ${JSON.stringify(error.message, null, 2)}`);
        }
      }, 2000);

    } catch (error) {
      console.error('Error in validation step:', error);
      setConfirmStep('result');
      setValidationResult('error');
      setValidationMessage('An error occurred during validation.');
      showFlashMessage('An error occurred during validation.', 'error');
    }
  };

  // Handle 3-step confirmation buttons
  const handleConfirmButton1 = () => {
    setConfirmButtonStep(1); // disable btn1
    setTimeout(() => {
      setConfirmButtonStep(2); // show btn2
    }, 1000);
  };

  const handleConfirmButton2 = () => {
    setConfirmButtonStep(3); // disable btn2
    setTimeout(() => {
      setConfirmButtonStep(4); // show btn3
    }, 1000);
  };

  const handleConfirmButton3 = () => {
    setShowConfirmDialog(false);
    setTimeout(() => {
      handleProcessImportOrder();
    }, 500);
  };

  // Reset confirmation dialog
  const resetConfirmDialog = () => {
    setShowConfirmDialog(false);
    setConfirmStep(null);
    setValidationResult(null);
    setValidationMessage('');
    setConfirmButtonStep(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box className="mb-6">
        <Box className="flex items-center gap-4 mb-4">
          <IconButton onClick={handleBack} className="text-gray-600">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" className="font-bold text-gray-800">
              {orderId ? 'Edit Import Order' : 'Create New Import Order'}
            </Typography>
            <Typography variant="body1" className="text-gray-600">
              {orderId ? orderData.unique_key : 'Enter order details'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Order Metadata */}
      <Card className="mb-6">
        <CardContent>
          <Box className="flex justify-between items-center mb-4">
            <Typography variant="h6" className="font-semibold">
              Order Information
            </Typography>
            <Box className="flex gap-2">
              {orderId && (
                <Chip
                  label={orderData.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                  color={orderData.status === 'confirmed' ? 'success' : 'warning'}
                  variant="filled"
                  className="font-semibold"
                />
              )}
              {isCreatingOrderPurchase && (
                <Chip
                  label="Creating Order Purchase..."
                  color="info"
                  variant="filled"
                  className="font-semibold"
                  icon={<CircularProgress size={16} />}
                />
              )}
            </Box>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Order ID"
                value={orderData.unique_key}
                onChange={(e) => setOrderData(prev => ({ ...prev, unique_key: e.target.value }))}
                fullWidth
                disabled={!isEditMode || !!orderId || isCreatingOrderPurchase}
                helperText={
                  orderId
                    ? "Order ID cannot be changed"
                    : orderData.unique_key && products.length > 1 && !orderPurchaseCreated
                      ? "Order purchase will be created automatically when you have more than 1 product"
                      : "Auto-generated with client ID + timestamp"
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Date Created"
                value={formatDate(orderData.createdAt)}
                fullWidth
                disabled
                helperText="Auto-generated"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Import Branch"
                value={getImportBranchDisplay(orderData.metaData?.clientId)}
                fullWidth
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Store fontSize="small" />
                    </InputAdornment>
                  )
                }}
                helperText="From order metadata"
              />
            </Grid>
            {/* Creator Names */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                  Creator
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: '40px', alignItems: 'center' }}>
                  {(orderData.creatorNames && orderData.creatorNames.length > 0) ? (
                    orderData.creatorNames.map((name, index) => (
                      <Chip
                        key={`creator-${index}`}
                        label={name}
                        size="small"
                        sx={{
                          backgroundColor: '#fbbf24',
                          color: '#78350f',
                          fontSize: '0.75rem',
                          height: '28px',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    ))
                  ) : (
                    <Chip
                      label="unknown"
                      size="small"
                      sx={{
                        backgroundColor: '#fbbf24',
                        color: '#78350f',
                        fontSize: '0.75rem',
                        height: '28px',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
            {/* Serial Inputter Names */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                  Serial Inputter
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: '40px', alignItems: 'center' }}>
                  {(orderData.serialInputterNames && orderData.serialInputterNames.length > 0) ? (
                    orderData.serialInputterNames.map((name, index) => (
                      <Chip
                        key={`serial-${index}`}
                        label={name}
                        size="small"
                        sx={{
                          backgroundColor: '#60a5fa',
                          color: '#1e3a8a',
                          fontSize: '0.75rem',
                          height: '28px',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    ))
                  ) : (
                    <Chip
                      label="unknown"
                      size="small"
                      sx={{
                        backgroundColor: '#60a5fa',
                        color: '#1e3a8a',
                        fontSize: '0.75rem',
                        height: '28px',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
            {/* Order Confirmer Names */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                  Order Confirmer
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: '40px', alignItems: 'center' }}>
                  {(orderData.orderConfirmerNames && orderData.orderConfirmerNames.length > 0) ? (
                    orderData.orderConfirmerNames.map((name, index) => (
                      <Chip
                        key={`confirmer-${index}`}
                        label={name}
                        size="small"
                        sx={{
                          backgroundColor: '#4ade80',
                          color: '#14532d',
                          fontSize: '0.75rem',
                          height: '28px',
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    ))
                  ) : (
                    <Chip
                      label="unknown"
                      size="small"
                      sx={{
                        backgroundColor: '#4ade80',
                        color: '#14532d',
                        fontSize: '0.75rem',
                        height: '28px',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Products List */}
      <Card>
        <CardContent>
          <Box className="flex justify-between items-center mb-4">
            <Box className="flex items-center gap-2">
              <Typography variant="h6" className="font-semibold">
                Products ({products.length})
              </Typography>
              {isAutoSaving && (
                <Chip
                  label="Saving..."
                  size="small"
                  color="info"
                  icon={<CircularProgress size={12} sx={{ color: 'white !important' }} />}
                  sx={{
                    height: '24px',
                    fontSize: '0.75rem'
                  }}
                />
              )}
            </Box>
            <Box className="flex gap-2">
              {(orderData.status === 'pending' || !orderId) && (
                <>
                  {canEdit && (
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddNewProduct}
                      className="bg-green-600 text-white hover:bg-green-700"
                    >
                      Add Product
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="outlined"
                      startIcon={<Upload />}
                      component="label"
                      disabled={csvImportLoading}
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Import CSV
                      <input
                        type="file"
                        accept=".csv"
                        hidden
                        onChange={handleFileUpload}
                      />
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="outlined"
                      startIcon={<Save />}
                      onClick={handleSaveOrder}
                      disabled={loading || products.length === 0}
                      className="bg-purple-600 text-white hover:bg-purple-700"
                    >
                      Save Order
                    </Button>
                  )}
                  {canConfirm && (
                    <Button
                      variant="contained"
                      onClick={handleConfirmAndProceed}
                      disabled={isConfirmButtonDisabled()}
                      className="bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400"
                      sx={{
                        minWidth: '160px',
                        fontWeight: 'bold'
                      }}
                    >
                      Confirm & Proceed
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead className="bg-gray-50">
                <TableRow>
                  <TableCell className="font-semibold">Product</TableCell>
                  <TableCell className="font-semibold">SKU</TableCell>
                  <TableCell className="font-semibold">Quantity</TableCell>
                  <TableCell className="font-semibold">Actual Qty</TableCell>
                  <TableCell className="font-semibold">% Imported</TableCell>
                  <TableCell className="font-semibold">Status</TableCell>
                  <TableCell className="font-semibold">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No products added yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product, index) => {
                    // Calculate total count including defects
                    const serialCount = product.serials ? product.serials.length : 0;
                    const defectsCount = (product.defects || product.Defects || []).length;
                    const totalCount = serialCount + defectsCount;
                    const quantity = product.qty || 0;
                    const isMatch = quantity === totalCount;
                    const hasDefects = defectsCount > 0;

                    // Calculate percentage
                    const percentage = quantity > 0 ? Math.round((totalCount / quantity) * 100) : 0;

                    // Determine color based on percentage
                    let percentageColor = '#666'; // Default gray
                    if (percentage < 30) {
                      percentageColor = '#dc2626'; // Red
                    } else if (percentage < 75) {
                      percentageColor = '#ca8a04'; // Yellow
                    } else if (percentage < 100) {
                      percentageColor = '#3b82f6'; // Light blue
                    } else {
                      percentageColor = '#16a34a'; // Dark green
                    }

                    return (
                      <TableRow
                        key={index}
                        className="hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                        onClick={() => handleViewProduct(product)}
                        sx={{
                          backgroundColor: hasDefects ? '#fffef0' : 'inherit', // Light yellow background if has defects
                          '&:hover': {
                            backgroundColor: hasDefects ? '#fffce5' : '#f5f5f5', // Slightly darker yellow on hover
                            transform: 'scale(1.01)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }
                        }}
                      >
                        <TableCell>
                          <Box className="flex items-center gap-3">
                            <Avatar
                              src={product.image_url}
                              sx={{ width: 40, height: 40 }}
                            >
                              <Inventory />
                            </Avatar>
                            <Typography variant="body2" className="font-medium">
                              {product.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell className="font-medium text-blue-600">
                          {product.SKU}
                        </TableCell>
                        <TableCell>{quantity}</TableCell>
                        <TableCell className="font-medium text-green-600">
                          {totalCount} {hasDefects && `(${serialCount} serials + ${defectsCount} defects)`}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            className="font-semibold"
                            sx={{ color: percentageColor }}
                          >
                            {percentage}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isMatch ? 'Match' : 'NotMatch'}
                            color={isMatch ? 'success' : 'error'}
                            variant="filled"
                            size="small"
                            className="font-semibold"
                          />
                        </TableCell>
                        <TableCell>
                          <Box className="flex items-center gap-1">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewProduct(product);
                              }}
                              className="text-blue-600"
                              title="View Details"
                            >
                              <Visibility />
                            </IconButton>
                            {canEdit && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteProduct(product._id);
                                }}
                                className="text-red-600"
                                title="Delete Product"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Floating Action Button for Add Product */}
      {(orderData.status === 'pending' || !orderId) && canEdit && (
        <Fab
          color="primary"
          aria-label="add product"
          onClick={handleAddNewProduct}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, #45a049 30%, #7cb342 90%)',
              color: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            }
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Product Detail Dialog */}
      {showProductDetail && (
        <ProductDetailDialog
          open={showProductDetail}
          onClose={handleCloseProductDetail}
          product={selectedProduct}
          onUpdateProduct={handleUpdateProduct}
          isEdit={isEditMode}
          existingProducts={products}
        />
      )}

      {/* Product Detail Modal */}
      {showProductDetailModal && selectedProduct && (
        <ProductDetailModal
          open={showProductDetailModal}
          onClose={handleCloseProductDetailModal}
          product={selectedProduct}
          onUpdateProduct={handleUpdateProductFromModal}
          allOrderSerials={getAllOrderSerials(selectedProduct._id)}
          orderStatus={orderData.status}
          orderId={orderId}
          allProducts={products}
          onRefreshData={loadOrderDetails}
          activeTags={activeTags}
        />
      )}

      {/* CSV Import Loading Modal */}
      <Dialog open={csvImportLoading} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box className="text-center py-6">
            <CircularProgress size={60} className="mb-4" />
            <Typography variant="h6" className="font-semibold mb-2">
              Importing CSV File
            </Typography>
            <Typography variant="body2" className="text-gray-600">
              {csvImportProgress || 'Processing...'}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* CSV Import Success/Error Messages */}
      {csvImportSuccess && (
        <Alert
          severity="success"
          className="fixed top-4 right-4 z-50 max-w-md"
          onClose={() => setCsvImportSuccess(null)}
        >
          {csvImportSuccess}
        </Alert>
      )}

      {csvImportError && (
        <Alert
          severity="error"
          className="fixed top-4 right-4 z-50 max-w-md"
          onClose={() => setCsvImportError(null)}
        >
          {csvImportError}
        </Alert>
      )}

      {/* Delete Order Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" className="font-semibold text-red-600">
            Delete Order
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" className="mb-4">
            Are you sure you want to delete this order? This action cannot be undone.
          </Typography>
          <Typography variant="body2" className="text-gray-600">
            Order ID: <strong>{orderData.unique_key}</strong>
          </Typography>
          <Typography variant="body2" className="text-gray-600">
            Products: <strong>{products.length}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            variant="outlined"
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteOrder}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Flash message snackbar */}
      <Snackbar
        open={isVisible}
        autoHideDuration={5000}
        onClose={hideMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={hideMessage}
          severity={type}
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>

      {/* Confirm & Proceed Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" className="font-semibold text-green-600">
            Confirm Warehouse Import
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" className="mb-4">
            Do you confirm to import the following products to the warehouse?
          </Typography>

          {/* Product Table */}
          <TableContainer component={Paper} className="mb-4">
            <Table>
              <TableHead className="bg-gray-50">
                <TableRow>
                  <TableCell className="font-semibold">Product</TableCell>
                  <TableCell className="font-semibold">SKU</TableCell>
                  <TableCell className="font-semibold">Quantity</TableCell>
                  <TableCell className="font-semibold">Actual Qty</TableCell>
                  <TableCell className="font-semibold">% Imported</TableCell>
                  <TableCell className="font-semibold">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product, index) => {
                  // Calculate total count including defects
                  const serialCount = product.serials ? product.serials.length : 0;
                  const defectsCount = (product.defects || product.Defects || []).length;
                  const totalCount = serialCount + defectsCount;
                  const quantity = product.qty || 0;
                  const isMatch = quantity === totalCount;
                  const hasDefects = defectsCount > 0;

                  // Calculate percentage
                  const percentage = quantity > 0 ? Math.round((totalCount / quantity) * 100) : 0;

                  // Determine color based on percentage
                  let percentageColor = '#666'; // Default gray
                  if (percentage < 30) {
                    percentageColor = '#dc2626'; // Red
                  } else if (percentage < 75) {
                    percentageColor = '#ca8a04'; // Yellow
                  } else if (percentage < 100) {
                    percentageColor = '#3b82f6'; // Light blue
                  } else {
                    percentageColor = '#16a34a'; // Dark green
                  }

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: hasDefects ? '#fffef0' : 'inherit', // Light yellow background if has defects
                      }}
                    >
                      <TableCell>
                        <Box className="flex items-center gap-3">
                          <Avatar
                            src={product.image_url}
                            sx={{ width: 32, height: 32 }}
                          >
                            <Inventory />
                          </Avatar>
                          <Typography variant="body2" className="font-medium">
                            {product.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell className="font-medium text-blue-600">
                        {product.SKU}
                      </TableCell>
                      <TableCell>{quantity}</TableCell>
                      <TableCell className="font-medium text-green-600">
                        {totalCount} {hasDefects && `(${serialCount} serials + ${defectsCount} defects)`}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          className="font-semibold"
                          sx={{ color: percentageColor }}
                        >
                          {percentage}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isMatch ? 'Match' : 'NotMatch'}
                          color={isMatch ? 'success' : 'error'}
                          variant="filled"
                          size="small"
                          className="font-semibold"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 3-Step Confirmation Buttons */}
          <Box className="mt-6">
            <Typography variant="body2" className="mb-4 text-gray-600 text-center">
              Press all 3 buttons in sequence to confirm
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              {/* Button 1 */}
              <Button
                variant="contained"
                onClick={handleConfirmButton1}
                disabled={confirmButtonStep >= 1}
                sx={{
                  flex: 1,
                  height: '56px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: confirmButtonStep >= 1 ? '#a5d6a7' : '#f57c00',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#e65100' },
                  '&.Mui-disabled': { backgroundColor: '#a5d6a7', color: '#fff' }
                }}
              >
                {confirmButtonStep >= 1 ? '1 ✓' : '1'}
              </Button>

              {/* Button 2 */}
              <Button
                variant="contained"
                onClick={handleConfirmButton2}
                disabled={confirmButtonStep < 2 || confirmButtonStep >= 3}
                sx={{
                  flex: 1,
                  height: '56px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: confirmButtonStep >= 3 ? '#a5d6a7' : confirmButtonStep >= 2 ? '#fb8c00' : '#e0e0e0',
                  color: confirmButtonStep >= 2 ? '#fff' : '#9e9e9e',
                  '&:hover': { backgroundColor: '#e65100' },
                  '&.Mui-disabled': {
                    backgroundColor: confirmButtonStep >= 3 ? '#a5d6a7' : '#e0e0e0',
                    color: confirmButtonStep >= 3 ? '#fff' : '#9e9e9e'
                  }
                }}
              >
                {confirmButtonStep >= 3 ? '2 ✓' : '2'}
              </Button>

              {/* Button 3 */}
              <Button
                variant="contained"
                onClick={handleConfirmButton3}
                disabled={confirmButtonStep < 4}
                sx={{
                  flex: 1,
                  height: '56px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  backgroundColor: confirmButtonStep >= 4 ? '#4CAF50' : '#e0e0e0',
                  color: confirmButtonStep >= 4 ? '#fff' : '#9e9e9e',
                  '&:hover': { backgroundColor: '#388E3C' },
                  '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
                }}
              >
                3
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Dialog */}
      <Dialog open={confirmStep === 'validation'} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box className="text-center py-6">
            <CircularProgress size={60} className="mb-4" />
            <Typography variant="h6" className="font-semibold mb-2">
              Validating Data
            </Typography>
            <Typography variant="body2" className="text-gray-600">
              {validationMessage || 'Please wait while we validate your import data...'}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Processing Dialog */}
      <Dialog open={confirmStep === 'processing'} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box className="text-center py-6">
            <CircularProgress size={60} className="mb-4" />
            <Typography variant="h6" className="font-semibold mb-2">
              Processing Import Order
            </Typography>
            <Typography variant="body2" className="text-gray-600">
              {validationMessage || 'Processing your import order...'}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={confirmStep === 'result'} onClose={resetConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" className={`font-semibold ${validationResult === 'success' ? 'text-green-600' : 'text-red-600'}`}>
            {validationResult === 'success' ? 'Import Successful' : 'Import Failed'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box className="text-center py-4">
            {validationResult === 'success' ? (
              <Box>
                <Box className="text-green-600 mb-4">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="currentColor" className="mx-auto">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                </Box>
                <Typography variant="h6" className="font-semibold mb-2 text-green-600">
                  Success!
                </Typography>
                <Typography variant="body1" className="text-gray-700">
                  {validationMessage}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box className="text-red-600 mb-4">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="currentColor" className="mx-auto">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </Box>
                <Typography variant="h6" className="font-semibold mb-2 text-red-600">
                  Error
                </Typography>
                <Typography variant="body1" className="text-gray-700">
                  {validationMessage || 'An error occurred while processing the import order.'}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetConfirmDialog} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Missing SKUs Modal */}
      <Dialog open={showMissingSKUsModal} onClose={() => { }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box className="flex items-center gap-2">
            <Box className="text-orange-600">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
              </svg>
            </Box>
            <Typography variant="h6" className="font-semibold text-orange-600">
              SKUs Not Found
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" className="mb-4">
            The following SKUs from your CSV file were not found in the system:
          </Typography>

          <Paper
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffecb5',
              mb: 3
            }}
          >
            <List>
              {missingSKUs.map((sku, index) => (
                <ListItem key={index} sx={{ borderBottom: index < missingSKUs.length - 1 ? '1px solid #ffecb5' : 'none' }}>
                  <ListItemText
                    primary={
                      <Box className="flex items-center gap-2">
                        <Box className="text-orange-600">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                          </svg>
                        </Box>
                        <Typography variant="body1" className="font-medium">
                          {sku}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{missingSKUs.length}</strong> SKU{missingSKUs.length !== 1 ? 's were' : ' was'} not found.
              {pendingValidProducts.length > 0 && (
                <> However, <strong>{pendingValidProducts.length}</strong> valid product{pendingValidProducts.length !== 1 ? 's were' : ' was'} found.</>
              )}
            </Typography>
          </Alert>

          <Typography variant="body2" className="text-gray-600">
            You can either skip these missing SKUs and continue with the valid products, or clear all data and try again with a corrected CSV file.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClearData}
            variant="outlined"
            color="error"
            startIcon={<Delete />}
          >
            Clear Data
          </Button>
          <Button
            onClick={handleSkipAndContinue}
            variant="contained"
            color="warning"
            disabled={pendingValidProducts.length === 0}
          >
            Skip and Continue ({pendingValidProducts.length} valid)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryImportCreatePage;
